import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'span'];
const ALLOWED_STYLES = new Set([
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-decoration',
]);

function filterStyle(value: string): string {
  return value
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => ALLOWED_STYLES.has(part.split(':')[0].trim().toLowerCase()))
    .join('; ');
}

const MARKUP_TAG = /<\/?[a-z][a-z0-9]*\b/i;
const ESCAPED_MARKUP = /&lt;\/?(?:p|br|strong|b|em|i|u|ul|ol|li|span)\b/i;

function escapeEditorText(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function unescapeAllowedMarkup(value?: string): string {
  const text = String(value || '');
  if (!ESCAPED_MARKUP.test(text)) return text;
  return text.replace(/&lt;/gi, '<').replace(/&gt;/gi, '>');
}

function hasMarkup(value: string): boolean {
  return MARKUP_TAG.test(value);
}

export function stripPresentationText(html?: string): string {
  return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function htmlFromAiInsert(raw: string): string {
  const trimmed = String(raw || '')
    .replace(/^```(?:html)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
  if (!trimmed) return '';
  if (/<\/?[a-z][\s\S]*>/i.test(trimmed)) return sanitizePresentationHtml(trimmed);
  const lines = trimmed.split(/\r?\n/).map((line) => line.replace(/^[-*•]\s+/, '').trim()).filter(Boolean);
  if (lines.length > 1) return sanitizePresentationHtml(lines.map((line) => `<p>${line}</p>`).join(''));
  return sanitizePresentationHtml(`<p>${trimmed}</p>`);
}

export function presentationEditorHtml(html?: string): string {
  const clean = sanitizePresentationHtml(unescapeAllowedMarkup(html));
  if (hasMarkup(clean)) return clean;
  const raw = stripPresentationText(clean) || stripPresentationText(html);
  return raw ? `<p>${escapeEditorText(raw)}</p>` : '';
}

export function sanitizePresentationHtml(html?: string): string {
  if (typeof window === 'undefined') {
    return String(html || '');
  }
  const clean = DOMPurify.sanitize(String(html || ''), {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['style'],
  });
  const root = document.createElement('div');
  root.innerHTML = clean;
  root.querySelectorAll('[style]').forEach((node) => {
    const filtered = filterStyle((node as HTMLElement).getAttribute('style') || '');
    if (filtered) (node as HTMLElement).setAttribute('style', filtered);
    else node.removeAttribute('style');
  });
  return root.innerHTML;
}

export type InlineTypographyKey = 'fontFamily' | 'fontSize' | 'fontWeight' | 'color';

const CSS_PROP: Record<InlineTypographyKey, string> = {
  fontFamily: 'font-family',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  color: 'color',
};

export function inlineTypographyKeysFromPatch(patch: Record<string, unknown>): InlineTypographyKey[] {
  return (Object.keys(CSS_PROP) as InlineTypographyKey[]).filter((key) => patch[key] != null);
}

export function stripInlineCssProps(html: string | undefined, keys: InlineTypographyKey[]): string {
  if (!html || !keys.length) return html || '';
  if (typeof window === 'undefined') return html;
  const names = new Set(keys.map((key) => CSS_PROP[key]));
  const root = document.createElement('div');
  root.innerHTML = html;
  root.querySelectorAll('[style]').forEach((node) => {
    const el = node as HTMLElement;
    const kept = (el.getAttribute('style') || '')
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => !names.has(part.split(':')[0].trim().toLowerCase()));
    if (kept.length) {
      el.setAttribute('style', kept.join('; '));
      return;
    }
    el.removeAttribute('style');
    if (el.tagName === 'SPAN' && el.attributes.length === 0) {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
  });
  return root.innerHTML;
}
